import { useState, useCallback, forwardRef } from 'react';
import { useSpring, animated } from 'react-spring';
import styled, { useTheme } from 'styled-components';

import { AccordionSimple } from '@hivebrite/design-system/Accordion';
import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { IconArrowDownHollow } from '@hivebrite/design-system/Icon';
import { CheckboxInput } from '@hivebrite/design-system/Inputs';
import { zIndices } from '@hivebrite/design-system/Theme';

import { getSectionsData } from 'hb-react/shared/apps/CookieBanner/utils/getSectionsData';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';

const AnimatedBox = animated(Box);

const Title = ({ isOpen, title, type, checked, handleChange, disabled }) => {
  const theme = useTheme();
  const springTitle = useSpring({
    rotate: isOpen ? 180 : 0,
  });

  return (
    <TitleStyled isOpen={isOpen}>
      <Box
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.stopPropagation();
            // Trigger checkbox change on Enter
            if (!disabled) {
              handleChange(type, e);
            }
          }
        }}
        mr={2}
        opacity={type === 'required' ? 0.5 : 1} // add this to checkbox component
      >
        <CheckboxInput
          checked={checked}
          disabled={disabled}
          hideLabel
          id={`${title}_checkbox`}
          label={title}
          onChange={(e) => {
            e.stopPropagation();
            handleChange(type, e);
          }}
        />
      </Box>
      <AnimatedBox
        color={theme.colors.primary}
        style={{
          transform: springTitle.rotate.to((rotate) => `rotate(${rotate}deg)`),
        }}
        mr={2}
        tabIndex={0}
        aria-expanded="false"
      >
        <IconArrowDownHollow size={30} />
      </AnimatedBox>
    </TitleStyled>
  );
};

const Content = forwardRef(({ cookieList, typeDescription }, ref) => (
  <Box ref={ref} flexDirection="column" width="80%">
    <Box ml={7}>{typeDescription}</Box>
    <Box ml={10} mb={3}>
      <StyledList>
        {cookieList.map((item, index) => (
          <li key={index}>
            <Box mt={3}>{item.description}</Box>
          </li>
        ))}
      </StyledList>
    </Box>
  </Box>
));

const AccordionTitle = ({
  checked,
  disabled,
  handleChangeCheckbox,
  isOpen,
  title,
  type,
}) => (
  <>
    <Separator />
    <Title
      checked={checked}
      disabled={disabled}
      handleChange={handleChangeCheckbox}
      isOpen={isOpen}
      title={title}
      type={type}
    />
  </>
);

const CustomiseSettingsModal = ({
  validateSelection,
  setDisplayModal,
  cookieList,
}) => {
  const [isOpenRef, setIsOpenRef] = useState(null);

  const onCloseModal = useCallback(() => {
    setDisplayModal(false);
  }, []);

  const [cookieTypes, setCookieTypes] = useState(
    getSectionsData(Content, cookieList),
  );

  const handleChangeCheckbox = (type) => {
    if (type === 'required') return null;

    setCookieTypes(
      cookieTypes.map((item) =>
        item.type === type ? { ...item, checked: !item.checked } : item,
      ),
    );
  };

  const handleAcceptSelected = () => {
    const listForValidation = [];

    cookieTypes.map((item) =>
      listForValidation.push({ type: item.type, accepted: item.checked }),
    );

    validateSelection('customiseSettings', listForValidation);
  };

  return (
    <ModalWithTemplate
      onCloseModal={onCloseModal}
      header={__('Customize privacy settings')}
      screenReadersCloseButtonText={s__(
        'CookieBannerAccessibility|Close customize privacy settings window',
      )}
      customZIndex={zIndices.modal + 1}
      footer={
        <Button onClick={handleAcceptSelected}>{__('Accept selected')}</Button>
      }
    >
      <Box width="100%" flexDirection="column">
        <Box width={{ _: '100%', m: '80%', l: '100%', xl: '100%' }} mb={3}>
          {s__(
            'CookieBanner|We use cookies to improve your experience on the platform. You can customize which categories of cookies you wish to accept. Open a category to see which cookies are included and what they are used for.',
          )}
        </Box>
        {cookieTypes.map(
          ({ title, type, checked, disabled, ...item }, index) => (
            <AccordionSimple
              key={index}
              isOpen={isOpenRef === index}
              onToggle={(isOpenRef) => setIsOpenRef(isOpenRef ? index : null)}
              title={({ isOpen }) => (
                <AccordionTitle
                  checked={checked}
                  disabled={disabled}
                  handleChangeCheckbox={() => handleChangeCheckbox(type)}
                  isOpen={isOpen}
                  title={title}
                  type={type}
                />
              )}
              {...item}
            />
          ),
        )}
        <Separator />
      </Box>
    </ModalWithTemplate>
  );
};

export default CustomiseSettingsModal;

const TitleStyled = styled(AnimatedBox)
  .withConfig({
    shouldForwardProp: (prop) => !['isOpen'].includes(prop),
  })
  .attrs(() => ({
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 40,
  }))`

  cursor: pointer;
  user-select: none;
`;

const Separator = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${(props) => props.theme.colors.horizon};
  margin-top: 10px;
  margin-bottom: 10px;
`;

const StyledList = styled.ul`
  list-style: disc;
`;
